import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import LaunchSandboxButton from "../components/LaunchSandboxButton";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

export default function DemoPage() {
  const [form, setForm] = React.useState({ name: "", email: "", company: "", team_size: "", notes: "" });
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/demo-request`, form);
      setSent(true);
      toast.success("Thanks — we'll reach out within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again or email support@fixlr.io.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="mk-demo">
      <section className="mk-section !pb-16 relative overflow-hidden">
        <div className="mk-grid-bg absolute inset-0 opacity-60 pointer-events-none" />
        <div className="mk-container relative z-10">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-4">
                Book a 20-min demo
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mk-display leading-[0.95]">
                See Fixlr<br /> in action.
              </h1>
              <p className="mt-6 text-lg text-[var(--mk-text-dim)] leading-relaxed max-w-xl">
                Walk us through one of your real workflows. We'll set up a sandbox with your own data so you can see exactly how the platform would feel on a Monday morning.
              </p>
              <ul className="mt-8 space-y-3 text-[var(--mk-text-dim)]">
                {[
                  "Live walkthrough tailored to your ops",
                  "Sandbox with your sample data pre-migrated",
                  "Honest answer on whether Fixlr fits or not",
                  "No sales pressure — we're engineers first",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm">
                    <span style={{ color: "var(--mk-accent)" }}>✓</span>
                    {t}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex gap-3 text-sm flex-wrap">
                <LaunchSandboxButton variant="primary" label="Launch live sandbox →" testId="mk-demo-sandbox" />
                <Link to="/signup" className="mk-btn mk-btn-secondary">Or start a free trial →</Link>
              </div>
              <div className="mk-mono text-[10px] text-[var(--mk-text-muted)] uppercase tracking-[0.2em] mt-3">
                Sandbox comes pre-loaded with demo jobs, operatives, quotes & invoices · 24h lifetime
              </div>
            </div>

            <div>
              {sent ? (
                <div
                  className="rounded-2xl border p-10 text-center space-y-4"
                  style={{ borderColor: "var(--mk-border-accent)", background: "rgba(214,239,55,0.06)" }}
                  data-testid="mk-demo-success"
                >
                  <div className="mk-mono text-[10px] uppercase tracking-[0.3em] text-[var(--mk-accent)]">Got it</div>
                  <div className="text-3xl font-black mk-display">We'll be in touch.</div>
                  <div className="text-sm text-[var(--mk-text-dim)]">
                    Someone from the Fixlr team will reach out within one business day to set up your demo slot.
                  </div>
                  <Link to="/product" className="mk-btn mk-btn-secondary text-sm mt-4">Explore the platform →</Link>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  className="rounded-2xl border p-6 md:p-8 space-y-4"
                  style={{ borderColor: "var(--mk-border-subtle)", background: "var(--mk-surface)" }}
                  data-testid="mk-demo-form"
                >
                  <div>
                    <label className="mk-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[var(--mk-text-muted)] block mb-1.5">Your name *</label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="w-full rounded-lg px-3 py-2.5 text-sm outline-none transition-colors"
                      style={{ background: "rgba(0,0,0,0.3)", border: "1px solid var(--mk-border-subtle)", color: "var(--mk-text)" }}
                      data-testid="mk-demo-name"
                    />
                  </div>
                  <div>
                    <label className="mk-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[var(--mk-text-muted)] block mb-1.5">Work email *</label>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-lg px-3 py-2.5 text-sm outline-none"
                      style={{ background: "rgba(0,0,0,0.3)", border: "1px solid var(--mk-border-subtle)", color: "var(--mk-text)" }}
                      data-testid="mk-demo-email"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mk-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[var(--mk-text-muted)] block mb-1.5">Company</label>
                      <input
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        className="w-full rounded-lg px-3 py-2.5 text-sm outline-none"
                        style={{ background: "rgba(0,0,0,0.3)", border: "1px solid var(--mk-border-subtle)", color: "var(--mk-text)" }}
                        data-testid="mk-demo-company"
                      />
                    </div>
                    <div>
                      <label className="mk-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[var(--mk-text-muted)] block mb-1.5">Team size</label>
                      <select
                        name="team_size"
                        value={form.team_size}
                        onChange={handleChange}
                        className="w-full rounded-lg px-3 py-2.5 text-sm outline-none"
                        style={{ background: "rgba(0,0,0,0.3)", border: "1px solid var(--mk-border-subtle)", color: "var(--mk-text)" }}
                        data-testid="mk-demo-team-size"
                      >
                        <option value="">—</option>
                        <option>1–5</option><option>6–20</option><option>21–50</option><option>51–200</option><option>200+</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="mk-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[var(--mk-text-muted)] block mb-1.5">Anything we should prep?</label>
                    <textarea
                      name="notes"
                      value={form.notes}
                      onChange={handleChange}
                      rows={3}
                      className="w-full rounded-lg px-3 py-2.5 text-sm outline-none resize-none"
                      style={{ background: "rgba(0,0,0,0.3)", border: "1px solid var(--mk-border-subtle)", color: "var(--mk-text)" }}
                      data-testid="mk-demo-notes"
                      placeholder="What's the workflow you most want to see?"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="mk-btn mk-btn-primary w-full justify-center text-base py-3 disabled:opacity-60"
                    data-testid="mk-demo-submit"
                  >
                    {submitting ? "Sending…" : "Request demo →"}
                  </button>
                  <div className="mk-mono text-[10px] text-[var(--mk-text-muted)] uppercase tracking-[0.2em] text-center">
                    We reply within 1 business day · no sales pressure
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
